import { Injectable } from '@angular/core';
import { OrderEditStateChangedAction } from 'app/00_modules/core/models/hubActions/order-edit-state-changed.action';
import { OrderEditingListAction } from 'app/00_modules/core/models/hubActions/order-editing-list.action';
import { OrderEnteringEditModeAction } from 'app/00_modules/core/models/hubActions/order-entering-edit-mode.action';
import { OrderLeavingEditModeAction } from 'app/00_modules/core/models/hubActions/order-leaving-edit-mode.action';
import { OrderRefreshEditModeAction } from 'app/00_modules/core/models/hubActions/order-refresh-editing-mode.action';
import { ChannelService } from 'app/00_modules/core/services/channel.service';
import { lastValueFrom, Observable } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { UserFacade } from '../../../store/facade/user.facade';
import { NotificationFacade } from '../../../store/facade/notification.facade';

/*
 * Warning: This is a statefull service with regards to Editing Mode: It handels events for entering and leaving editing mode
 * of an order of the current user.
 * Additionaly, it handels editing status of list-items
 */
@Injectable({
    providedIn: 'root',
})
export class EditorsService {
    public orderId: number;
    public user: string;
    private interval: any;

    constructor(
        private channel: ChannelService,
        private userFacade: UserFacade,
        private notificationFacade: NotificationFacade,
    ) {}

    public async init(orderId: number): Promise<Observable<string[]>> {
        this.channel.switchOnAction(new OrderEditStateChangedAction(this.notificationFacade));

        this.user = await lastValueFrom(this.userFacade.fullName$.pipe(take(1)));
        if (orderId) {
            this.orderId = orderId;
        } else {
            throw new Error('Unable to access order id for editor');
        }

        this.notificationFacade.setOrderInEditMode({
            orderId: this.orderId,
            user: this.user,
        });

        this.notificationFacade.addAllOrder([
            {
                id: 0,
                orderId: this.orderId,
                users: [this.user],
            },
        ]);

        const action = new OrderEnteringEditModeAction(this.user, this.orderId);
        await this.channel.invokeAction(action);
        await this.startRefreshEditingMode();

        return this.notificationFacade.orders$.pipe(
            filter(orders => !!orders),
            map(orders => orders.filter(order => order.orderId === this.orderId)),
            map(orders => orders[0].users.filter(u => u !== this.user)),
        );
    }

    public async release(): Promise<void> {
        this.stopRefreshEditingMode();
        this.notificationFacade.resetOrderInEditMode();
        this.channel.switchOffAction(new OrderEditStateChangedAction(this.notificationFacade));
        const action = new OrderLeavingEditModeAction(this.user, this.orderId);
        await this.channel.invokeAction(action);
    }

    public async initForListItem(orderId: number): Promise<Observable<string[]>> {
        await this.channel.switchOnAction(new OrderEditStateChangedAction(this.notificationFacade));
        const action = new OrderEditingListAction(orderId);
        await this.channel.invokeAction(action);
        this.user = await lastValueFrom(this.userFacade.fullName$.pipe(take(1)));

        return this.notificationFacade.orders$.pipe(
            map(orders => (orders ?? []).find(order => order.orderId === orderId)),
            map(o => (o ? o.users.filter(u => u !== this.user) : [])),
        );
    }

    public async releaseList(): Promise<void> {
        this.channel.switchOffAction(new OrderEditStateChangedAction(null));
    }

    private async startRefreshEditingMode(): Promise<void> {
        this.interval = setInterval(async () => this.refreshEditingMode(), 250000);
    }

    private stopRefreshEditingMode(): void {
        clearInterval(this.interval);
    }

    private async refreshEditingMode(): Promise<void> {
        const action = new OrderRefreshEditModeAction(this.user, this.orderId);
        await this.channel.invokeAction(action);
    }
}
